<template>
  <div class="removal-setting-page">
    <PageHeader
      title="자동삭제 설정"
      description="재등록을 위해 상품을 자동 삭제하는 기준을 설정합니다."
      :margin-bottom="true"
    />

    <div class="removal-setting-body">
      <div class="removal-setting-card">
        <AppLoading v-if="loading" text="자동삭제 설정을 불러오는 중입니다..." />
        <template v-else>
          <div class="section-header">
            <h3>기본 설정</h3>
            <el-tag :type="form.deletionEnabled ? 'success' : 'info'" size="large">
              {{ form.deletionEnabled ? '자동삭제 사용중' : '자동삭제 중지' }}
            </el-tag>
          </div>

          <div class="setting-list">
            <div class="setting-item">
              <div>
                <label>삭제 진행 여부</label>
                <p>조건에 맞는 상품을 매일 자동으로 삭제합니다.</p>
              </div>
              <el-switch v-model="form.deletionEnabled" />
            </div>

            <div class="setting-item">
              <div>
                <label>모든 마켓 공통 삭제 개수</label>
                <p>매일 자동 삭제할 상품 개수입니다.</p>
              </div>
              <el-input v-model="form.marketDeleteCount" inputmode="numeric" class="short-input" />
            </div>

            <div class="setting-item">
              <div>
                <label>등록 후 최소 경과 일수</label>
                <p>등록 직후 상품이 바로 삭제되지 않도록 최소 일수를 둡니다.</p>
              </div>
              <el-input v-model="form.minRegisteredDays" inputmode="numeric" class="short-input" />
            </div>

            <div class="setting-item">
              <div>
                <label>삭제추천 최소 조회수</label>
                <p>삭제추천으로 분류할 최소 조회수 기준입니다.</p>
              </div>
              <el-input
                v-model="form.minRecommendedViewCount"
                inputmode="numeric"
                class="short-input"
              />
            </div>
          </div>

          <div class="summary-box">
            <h4>적용 기준</h4>
            <p v-if="form.deletionEnabled">
              등록 후 {{ formatNumber(form.minRegisteredDays) }}일 이상 지난 상품 중 삭제추천
              기준을 만족한 상품을 대상으로, 마켓별 최대 {{ formatNumber(form.marketDeleteCount) }}개까지
              자동삭제합니다.
            </p>
            <p v-else>
              자동삭제가 꺼져 있습니다. 기준값은 저장되지만 실제 삭제는 진행되지 않습니다.
            </p>
          </div>

          <div class="action-row">
            <el-button @click="loadSetting">다시 불러오기</el-button>
            <el-button
              type="primary"
              :loading="submitLoading"
              :disabled="!canSubmit"
              @click="handleSubmit"
            >
              저장
            </el-button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import AppLoading from '@/components/common/loading.vue';
import PageHeader from '@/components/common/header.vue';
import { fetchRemovalSetting, updateRemovalSetting } from '@/services/n2n';

const createDefaultForm = () => ({
  deletionEnabled: false,
  marketDeleteCount: '0',
  minRegisteredDays: '0',
  minRecommendedViewCount: '0'
});

export default {
  name: 'N2nRemovalSetting',
  components: {
    AppLoading,
    PageHeader
  },
  setup() {
    const loading = ref(false);
    const submitLoading = ref(false);
    const form = reactive(createDefaultForm());

    const normalizeNumber = value => {
      const sanitized = String(value ?? '').replace(/[^\d]/g, '');
      const num = Number(sanitized);
      if (!Number.isFinite(num) || num < 0) return 0;
      return Math.floor(num);
    };

    const applyForm = payload => {
      const next = payload || {};
      form.deletionEnabled = Boolean(next.deletionEnabled);
      form.marketDeleteCount = String(normalizeNumber(next.marketDeleteCount));
      form.minRegisteredDays = String(normalizeNumber(next.minRegisteredDays));
      form.minRecommendedViewCount = String(normalizeNumber(next.minRecommendedViewCount));
    };

    const formatNumber = value => {
      const num = Number(value);
      if (!Number.isFinite(num)) return '0';
      return num.toLocaleString();
    };

    const canSubmit = computed(() => {
      return (
        typeof form.deletionEnabled === 'boolean' &&
        Number.isInteger(normalizeNumber(form.marketDeleteCount)) &&
        Number.isInteger(normalizeNumber(form.minRegisteredDays)) &&
        Number.isInteger(normalizeNumber(form.minRecommendedViewCount))
      );
    });

    const buildPayload = () => ({
      deletionEnabled: Boolean(form.deletionEnabled),
      marketDeleteCount: normalizeNumber(form.marketDeleteCount),
      minRegisteredDays: normalizeNumber(form.minRegisteredDays),
      minRecommendedViewCount: normalizeNumber(form.minRecommendedViewCount)
    });

    const loadSetting = async () => {
      loading.value = true;
      try {
        const { data } = await fetchRemovalSetting();
        applyForm(data?.data ?? data);
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '자동삭제 설정을 불러오지 못했습니다.');
      } finally {
        loading.value = false;
      }
    };

    const handleSubmit = async () => {
      const payload = buildPayload();
      submitLoading.value = true;
      try {
        const { data } = await updateRemovalSetting(payload);
        applyForm(data?.data ?? payload);
        ElMessage.success(data?.message || '자동삭제 설정이 저장되었습니다.');
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '자동삭제 설정 저장에 실패했습니다.');
      } finally {
        submitLoading.value = false;
      }
    };

    onMounted(() => {
      loadSetting();
    });

    return {
      loading,
      submitLoading,
      form,
      canSubmit,
      formatNumber,
      loadSetting,
      handleSubmit
    };
  }
};
</script>

<style scoped>
.removal-setting-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--el-bg-color-page);
  overflow-y: auto;
}

.removal-setting-body {
  padding: var(--spacing-lg);
  width: 100%;
  max-width: 880px;
  margin: 0 auto;
  box-sizing: border-box;
}

.removal-setting-card {
  background: var(--el-bg-color);
  border: 1px solid var(--el-border-color-lighter);
  border-radius: var(--el-border-radius-base);
  padding: 28px;
  box-shadow: var(--el-box-shadow-light);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.section-header h3 {
  margin: 0;
  font-size: 18px;
  color: var(--el-text-color-primary);
}

.setting-list {
  border-top: 1px solid var(--el-border-color-lighter);
}

.setting-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 148px;
  align-items: center;
  gap: 20px;
  padding: 16px 0;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.setting-item :deep(.el-switch) {
  justify-self: flex-end;
}

.setting-item label {
  display: block;
  margin-bottom: 5px;
  font-size: 14px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.setting-item p {
  margin: 0;
  font-size: 13px;
  color: var(--el-text-color-secondary);
  line-height: 1.5;
}

.short-input {
  width: 148px;
}

.summary-box {
  margin-top: 20px;
  padding: 14px 16px;
  border-radius: 10px;
  background: var(--el-fill-color-lighter);
}

.summary-box h4 {
  margin: 0 0 8px;
  font-size: 15px;
  color: var(--el-text-color-primary);
}

.summary-box p {
  margin: 0;
  color: var(--el-text-color-regular);
  line-height: 1.6;
}

.action-row {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}

@media (max-width: 900px) {
  .setting-item {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .setting-item :deep(.el-switch) {
    justify-self: flex-start;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 768px) {
  .removal-setting-body {
    padding: var(--spacing-sm);
  }

  .removal-setting-card {
    padding: 16px;
  }
}
</style>
